import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import routes from 'routes';
import { statusFilterOptions, columns } from 'config/admin/competition';
import { getSeasonMeets } from 'api/clubApi';
import { getSeasons } from 'api/season';
import { getSeasonBusinessRules } from 'services/admin/SeasonService';
import { getClubRole, getUserPermissionLevel } from 'services/auth/tokenService';
import { meetsToTableData } from 'utils/meet';
import {
  MEET_TYPE,
  meetTypeName,
  meetTypeOptionsFor,
} from 'utils/clubFeatures';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import AdminPageHeader from 'components/admin/AdminPageHeader';
import AdminDataTable from 'components/admin/AdminDataTable';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import { UserPermissionLevel } from 'constants/auth';

const STATUS_ORDER = { Draft: 0, Upcoming: 1, Complete: 2 };

// One track per column in config/admin/competition.js: date, name, location, athletes,
// counts for points, counts for season bests, status, buttons. Shared by the three tables on
// this page; keep it in step with that config when a column is added or removed.
const MEET_TABLE_COLUMNS = '5fr 4fr 4fr 3fr 2fr 2fr 3fr 4fr';

// "All", then the meet types the club may have, as the club reads them (a
// school: Competition and Training, with no multi-club meet).
const meetTypeFilterOptionsFor = (club) => [
  { value: 'all', label: 'All' },
  ...meetTypeOptionsFor(club, 'filter'),
];

const Meets = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userPermissionLevel =
    parseInt(getUserPermissionLevel(), 10) || UserPermissionLevel.None;

  // Guests and visitors (Guest tier) are limited to the CURRENT season on the meets list: they can't
  // list all seasons (the seasons endpoint is Member+) and must not browse past seasons. Members+ can.
  const canBrowseSeasons = userPermissionLevel >= UserPermissionLevel.Member;

  // Visitors can only view multi-club meets, so the meet-type filter is meaningless for them — hide it.
  // (Guests keep it: they have full access to all meet types.)
  const isVisitor = getClubRole() === 'Visitor';

  const { selectedClub } = useSelector((state) => state.user || {});

  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonID, setSelectedSeasonID] = useState('');
  const [selectedMeetType, setSelectedMeetType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const fromUrl = searchParams.get('status');
    const match = statusFilterOptions.find((opt) => opt.value === fromUrl);
    if (!match) return 'all';
    if (
      match.permissionLevel &&
      (parseInt(getUserPermissionLevel(), 10) || UserPermissionLevel.None) <
        match.permissionLevel
    ) {
      return 'all';
    }
    return match.value;
  });
  const [meets, setMeets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initial mount fires getSeasonMeets without a seasonID (server returns the
  // active season's meets) in parallel with getSeasons. Once seasons resolve we
  // set the dropdown to the active season's ID — that state change would
  // trigger a duplicate fetch for the same data, so we skip it once.
  const skipNextFetchRef = useRef(false);

  const visibleStatusOptions = useMemo(
    () =>
      statusFilterOptions.filter(
        (opt) =>
          !opt.permissionLevel || userPermissionLevel >= opt.permissionLevel
      ),
    [userPermissionLevel]
  );

  const seasonOptions = useMemo(
    () => seasons.map((s) => ({ value: s.seasonID, label: s.name })),
    [seasons]
  );

  const fetchSeasons = useCallback(async () => {
    if (!selectedClub?.clubID) return;
    // Guest tier is locked to the current season — skip the Member+ seasons list (would 403) and let
    // fetchMeets fall back to the active season via getSeasonMeets(null).
    if (!canBrowseSeasons) return;
    try {
      const response = await getSeasons(selectedClub.clubID);
      const seasonsData = response.data || [];
      const { activeSeason, sortedSeasons } =
        getSeasonBusinessRules(seasonsData);
      // Season rollover accumulates past seasons, but the meets list only needs
      // recent history: offer the current (and any draft) season plus the most
      // recent previous season — older seasons are dropped from the dropdown.
      // sortedSeasons is ordered Draft, Active, then past seasons newest-first.
      const mostRecentPastSeason = sortedSeasons.find(
        (s) => s.status !== 'Active' && s.status !== 'Draft'
      );
      const visibleSeasons = sortedSeasons.filter(
        (s) =>
          s.status === 'Active' ||
          s.status === 'Draft' ||
          s === mostRecentPastSeason
      );
      setSeasons(visibleSeasons);
      if (activeSeason?.seasonID) {
        skipNextFetchRef.current = true;
        setSelectedSeasonID(activeSeason.seasonID);
      } else if (visibleSeasons.length > 0) {
        setSelectedSeasonID(visibleSeasons[0].seasonID);
      }
    } catch (err) {
      console.log('[Meets] Fetch seasons error: ', err);
    }
  }, [selectedClub?.clubID, canBrowseSeasons]);

  const fetchMeets = useCallback(async () => {
    if (!selectedClub?.clubID) return;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    setLoading(true);
    try {
      const response = await getSeasonMeets(
        selectedClub.clubID,
        selectedSeasonID || null
      );
      setMeets(meetsToTableData(response.data, selectedClub.clubID));
    } catch (err) {
      console.log('[Meets] Fetch season meets error: ', err);
      setMeets([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClub?.clubID, selectedSeasonID]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  useEffect(() => {
    fetchMeets();
  }, [fetchMeets]);

  const { clubCompetitions, multiClubCompetitions, trainingSessions } =
    useMemo(() => {
      let filtered = meets;

      if (userPermissionLevel < UserPermissionLevel.Official) {
        filtered = filtered.filter((m) => m.meetStatus !== 'Draft');
      }

      if (selectedStatus !== 'all') {
        filtered = filtered.filter((m) => m.meetStatus === selectedStatus);
      }

      if (selectedMeetType !== 'all') {
        filtered = filtered.filter((m) => m.meetType === selectedMeetType);
      }

      const club = filtered.filter((m) => m.meetType === 1);
      const multi = filtered.filter((m) => m.meetType === 2);
      const trains = filtered.filter((m) => m.meetType === 3);

      if (selectedStatus === 'all') {
        const byStatus = (a, b) =>
          (STATUS_ORDER[a.meetStatus] ?? 99) -
          (STATUS_ORDER[b.meetStatus] ?? 99);
        club.sort(byStatus);
        multi.sort(byStatus);
        trains.sort(byStatus);
      }

      return {
        clubCompetitions: club,
        multiClubCompetitions: multi,
        trainingSessions: trains,
      };
    }, [meets, selectedStatus, selectedMeetType, userPermissionLevel]);

  return (
    <>
      <MetaTags title="SplitFast | Competitions" />
      <AdminTablePageLayout loading={loading}>
        <AdminPageHeader showNumber={false} name="Meet" />
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'flex-end',
            marginBottom: 16,
          }}
        >
          {canBrowseSeasons && (
            <div style={{ width: 260 }}>
              <CustomDropdown
                label="Season"
                name="season"
                value={selectedSeasonID}
                options={seasonOptions}
                onChange={(_, value) => setSelectedSeasonID(value)}
                placeholder="Select season"
              />
            </div>
          )}
          {!isVisitor && (
            <div style={{ width: 220 }}>
              <CustomDropdown
                label="Meet Type"
                name="meetType"
                value={selectedMeetType}
                options={meetTypeFilterOptionsFor(selectedClub)}
                onChange={(_, value) => setSelectedMeetType(value)}
                placeholder="Select meet type"
              />
            </div>
          )}
          <div style={{ width: 200 }}>
            <CustomDropdown
              label="Status"
              name="status"
              value={selectedStatus}
              options={visibleStatusOptions}
              onChange={(_, value) => setSelectedStatus(value)}
              placeholder="Select status"
            />
          </div>
          {userPermissionLevel >= UserPermissionLevel.Official && (
            <div style={{ marginLeft: 'auto' }}>
              <CustomNewButton
                onClick={() =>
                  navigate(routes.admin.addMeet.url(selectedClub?.clubID))
                }
                label="+ Add a Meet"
              ></CustomNewButton>
            </div>
          )}
        </div>
        {clubCompetitions.length > 0 && (
          <div>
            <h2 className="heading2" style={{ margin: '20px 0' }}>
              {meetTypeName(selectedClub, MEET_TYPE.SINGLE_CLUB, 'heading')}
            </h2>
            <AdminDataTable
              columns={columns}
              data={clubCompetitions}
              searchInputPlaceholder={`Search ${meetTypeName(selectedClub, MEET_TYPE.SINGLE_CLUB, 'heading')}`}
              headStyle={{
                gridTemplateColumns: MEET_TABLE_COLUMNS,
              }}
              rowStyle={{
                gridTemplateColumns: MEET_TABLE_COLUMNS,
              }}
            />
          </div>
        )}
        {multiClubCompetitions.length > 0 && (
          <div>
            <h2 className="heading2" style={{ margin: '20px 0' }}>
              Multi-club Competitions
            </h2>
            <AdminDataTable
              columns={columns}
              data={multiClubCompetitions}
              searchInputPlaceholder="Search Multi-club Competitions"
              headStyle={{
                gridTemplateColumns: MEET_TABLE_COLUMNS,
              }}
              rowStyle={{
                gridTemplateColumns: MEET_TABLE_COLUMNS,
              }}
            />
          </div>
        )}
        {trainingSessions.length > 0 && (
          <div>
            <h2 className="heading2" style={{ margin: '20px 0' }}>
              Training Sessions
            </h2>
            <AdminDataTable
              columns={columns}
              data={trainingSessions}
              searchInputPlaceholder="Search Training Sessions"
              headStyle={{
                gridTemplateColumns: MEET_TABLE_COLUMNS,
              }}
              rowStyle={{
                gridTemplateColumns: MEET_TABLE_COLUMNS,
              }}
            />
          </div>
        )}
        {!clubCompetitions.length &&
          !multiClubCompetitions.length &&
          !trainingSessions.length && (
            <div style={{ textAlign: 'center' }}>
              No Meets with the selected filters found for the selected season
            </div>
          )}
      </AdminTablePageLayout>
    </>
  );
};

export default Meets;
